import { Modal } from "./Modal";
import { useSellableLand } from "./useSellLand";
import { useMarketplaceConfig } from "./useMarketplace";
import { EmptyState, Tag } from "./primitives";
import { useT } from "../../i18n";
import type { SellSourceParcel } from "./SellLandWizard";
import type { SellableParcel } from "../../lib/marketplace-api";

/** Entry A — MY MARKET → LIST LAND → choose one of the session wallet's parcels. */
export function SellLandParcelPicker({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (p: SellSourceParcel) => void;
}) {
  const t = useT();
  const { data: config } = useMarketplaceConfig();
  const { data, isLoading } = useSellableLand(open);
  const parcels = data?.parcels ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("marketplace.sell.pickTitle")}
      subtitle={config?.preview_demo ? t("marketplace.sell.previewDemo") : undefined}
      testId="sell-parcel-picker"
      size="md"
    >
      <p className="mb-4 font-mono text-[10.5px] leading-relaxed text-text-muted">{t("marketplace.sell.pickHint")}</p>
      {isLoading ? (
        <div data-testid="sell-picker-loading" className="py-10 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-text-muted animate-pulse">
          {t("marketplace.states.loading")}
        </div>
      ) : parcels.length === 0 ? (
        <EmptyState testId="sell-picker-empty" title={t("marketplace.sell.pickEmpty")} hint={t("marketplace.sell.pickEmptyHint")} />
      ) : (
        <ul className="space-y-2" data-testid="sell-picker-list">
          {parcels.map((p: SellableParcel) => (
            <li key={p.parcel_id}>
              <button
                type="button"
                data-testid={`sell-picker-parcel-${p.parcel_id}`}
                onClick={() =>
                  onPick({ parcel_id: p.parcel_id, x: p.x, y: p.y, w: p.w, h: p.h, owner: p.owner })
                }
                className="w-full flex items-center justify-between gap-3 rounded-lg border border-border-divider bg-elevated/40 px-4 py-3 hover:border-neon-teal/50 hover:bg-elevated transition-[border-color,background-color] text-left"
              >
                <div className="flex flex-col">
                  <span className="font-display text-sm text-text-primary">Parcel #{p.parcel_id}</span>
                  <span className="font-mono text-[10px] text-text-muted tabular-nums">
                    {p.x},{p.y} · {p.w}×{p.h} · {t("marketplace.sell.pickCells", { n: p.cell_count })}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {p.active_listings > 0 && <Tag tone="coral">{t("marketplace.sell.pickListed", { n: p.active_listings })}</Tag>}
                  <span className="font-mono text-[10.5px] text-neon-teal tracking-[0.12em] uppercase">
                    {t("marketplace.sell.pickOpen")}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
